import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactECharts from 'echarts-for-react';
import { ProductionBandBreakdown, ProductionPipeline } from '../hooks/useOverviewData';
import { formatNumber } from '../utils/format';
import { AV2_CHART } from '../utils/chartTheme';

interface Props {
    data: ProductionPipeline | null;
    loading: boolean;
}

// Done=green, In progress=yellow, Waiting=blue, Late=red.
const STATUS_DEFS: { key: keyof ProductionBandBreakdown; color: string; labelId: string; labelDefault: string }[] = [
    { key: 'done', color: '#10b981', labelId: 'analyticsV2.pipeline.status.done', labelDefault: 'Done' },
    { key: 'in_progress', color: '#eab308', labelId: 'analyticsV2.pipeline.status.inProgress', labelDefault: 'In progress' },
    { key: 'waiting', color: '#3b82f6', labelId: 'analyticsV2.pipeline.status.waiting', labelDefault: 'Waiting' },
    { key: 'late', color: '#ef4444', labelId: 'analyticsV2.pipeline.status.late', labelDefault: 'Late' },
];

/**
 * Production pipeline as two donuts — one per band (planning / production) —
 * sliced by the status of each product in that band (Done / In progress /
 * Waiting / Late). A `minAngle` keeps low-count slices visible as real wedges,
 * and outside labels keep their counts readable. The headline total + completed
 * sit in a compact caption above. (Backend: AnalyticsRepository::productionPipeline.)
 */
const ProductionPipelineCard: React.FC<Props> = ({ data, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    if (loading || !data) {
        return <ProductionPipelineSkeleton />;
    }

    const isEmpty = data.total === 0 && data.completed === 0;

    const planningLabel = intl.formatMessage({ id: 'analyticsV2.pipeline.planning', defaultMessage: 'In planning / design' });
    const productionLabel = intl.formatMessage({ id: 'analyticsV2.pipeline.production', defaultMessage: 'In production' });
    const bands: { label: string; data: ProductionBandBreakdown }[] = [
        { label: planningLabel, data: data.planning },
        { label: productionLabel, data: data.production },
    ];
    const statusLabel = (s: typeof STATUS_DEFS[number]) => intl.formatMessage({ id: s.labelId, defaultMessage: s.labelDefault });

    const donutSeries = (center: string, band: { label: string; data: ProductionBandBreakdown }) => ({
        name: band.label,
        type: 'pie',
        center: [center, '46%'],
        radius: ['40%', '60%'],
        minAngle: 12, // keep low-count slices visible as a real wedge
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#ffffff', borderWidth: 2 },
        label: {
            show: true,
            position: 'outside',
            formatter: '{c}',
            color: AV2_CHART.textSecondary,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: AV2_CHART.font,
        },
        labelLine: { show: true, length: 8, length2: 8 },
        data: STATUS_DEFS.filter((s) => band.data[s.key] > 0).map((s) => ({
            name: statusLabel(s),
            value: band.data[s.key],
            itemStyle: { color: s.color },
        })),
    });

    const option: any = {
        textStyle: { fontFamily: AV2_CHART.font },
        tooltip: {
            trigger: 'item',
            backgroundColor: AV2_CHART.tooltipBg,
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: '#ffffff', fontSize: 12, fontFamily: AV2_CHART.font },
            valueFormatter: (v: number) => formatNumber(Number(v) || 0, locale),
        },
        legend: {
            bottom: 0,
            data: STATUS_DEFS.map(statusLabel),
            icon: 'roundRect',
            itemWidth: 11,
            itemHeight: 11,
            itemGap: 16,
            textStyle: { color: AV2_CHART.textSecondary, fontSize: 12, fontFamily: AV2_CHART.font },
        },
        title: [
            { text: planningLabel, left: '25%', top: '84%', textAlign: 'center', textStyle: { color: AV2_CHART.textSecondary, fontSize: 12, fontWeight: 600, fontFamily: AV2_CHART.font } },
            { text: productionLabel, left: '75%', top: '84%', textAlign: 'center', textStyle: { color: AV2_CHART.textSecondary, fontSize: 12, fontWeight: 600, fontFamily: AV2_CHART.font } },
        ],
        series: [donutSeries('25%', bands[0]), donutSeries('75%', bands[1])],
    };

    return (
        <section className="av2-card av2-pipeline">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.pipeline.title" defaultMessage="Production pipeline" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage id="analyticsV2.pipeline.subtitle" defaultMessage="Where production stands right now" />
                    </p>
                </div>
            </div>

            {isEmpty ? (
                <div className="av2-empty">
                    <FormattedMessage id="analyticsV2.pipeline.empty" defaultMessage="No products in production." />
                </div>
            ) : (
                <div className="av2-pipeline__row">
                    <div className="av2-pipeline__stat">
                        <span className="av2-pipeline__stat-value">{formatNumber(data.total, locale)}</span>
                        <span className="av2-pipeline__stat-label">
                            <FormattedMessage id="analyticsV2.pipeline.total" defaultMessage="In the pipeline" />
                        </span>
                    </div>
                    <div className="av2-pipeline__chart">
                        <ReactECharts option={option} notMerge style={{ height: 300, width: '100%' }} opts={{ renderer: 'svg' }} />
                    </div>
                    <div className="av2-pipeline__stat">
                        <span className="av2-pipeline__stat-value">{formatNumber(data.completed, locale)}</span>
                        <span className="av2-pipeline__stat-label">
                            <FormattedMessage id="analyticsV2.pipeline.completed" defaultMessage="Completed in period" />
                        </span>
                    </div>
                </div>
            )}
        </section>
    );
};

const ProductionPipelineSkeleton: React.FC = () => (
    <section className="av2-card av2-pipeline">
        <div className="av2-card__header">
            <div className="av2-skel av2-skel--card-title" />
        </div>
        <div className="av2-pipeline__row">
            <div className="av2-pipeline__stat">
                <div className="av2-skel" style={{ width: 56, height: 38, borderRadius: 'var(--av2-radius-md)' }} />
                <div className="av2-skel av2-skel--label" style={{ marginTop: 6 }} />
            </div>
            <div className="av2-pipeline__chart">
                <div className="av2-skel" style={{ width: '100%', height: 280, borderRadius: 'var(--av2-radius-md)' }} />
            </div>
            <div className="av2-pipeline__stat">
                <div className="av2-skel" style={{ width: 56, height: 38, borderRadius: 'var(--av2-radius-md)' }} />
                <div className="av2-skel av2-skel--label" style={{ marginTop: 6 }} />
            </div>
        </div>
    </section>
);

export default ProductionPipelineCard;
